import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryDataSource } from '../../api/query';
import { raporBul, sourceIdBul } from '../../utils/raporSonek';
import { lokasyonDestekli, lokasyonParam, magazaSuz } from './subeSuzgeci';
import { KapsamNotu } from './KapsamNotu';
import { SectionCard, fmtTRY, fmtNum } from '../yeni-design/primitives';
import type { ReportConfig } from '../../types/panelConfig';
import { oncekiDonemHesapla } from './donem';
import type { Donem } from './donem';

interface ZararSatiri {
  urun: string;
  kategori: string;
  satilan_miktar: number;
  ort_satis_fiyat: number;
  birim_maliyet: number;
  zarar: number;
  /**
   * S197 - YALNIZ 990041 "Zararına Satışlar (maliyet doğrulamalı)" bu uc
   * kolonu tasir. 990033 tasimaz; o zaman hepsi undefined kalir ve ayrim
   * HIC yapilmaz (eski davranis birebir korunur).
   */
  alinan_miktar?: number | string | null;
  gercek_birim_maliyet?: number | string | null;
  gercek_zarar?: number | string | null;
}

interface IadeSatiri {
  magaza: string;
  iade_fis: number;
  iade_tutar: number;
  satis_tutar: number;
  iade_oran: number;
}

interface KategoriSatiri {
  kategori: string;
  ciro: number;
  miktar: number;
  kar: number;
  pay_yuzde: number;
}

interface MarjErimeSatiri {
  kategori: string;
  marjBu: number;
  marjOnceki: number;
  fark: number;
}

interface Props {
  reports: ReportConfig[];
  donem: Donem;
  /** null = tum subeler. 011 `magaza` kolonu tasir -> suzulur.
   *  033/041 ve 015 subeye AYRISMAZ -> o bolumler acikca etiketlenir. */
  sube?: string | null;
}

/**
 * Sabit fiyatli kategoriler: urun devlet-belirledigi etiket fiyatindan
 * satilir. Zam sonrasi son_alis_fiyat, satisin yapildigi andaki eski
 * etiketin uzerine cikar -> sistematik ama SAHTE zarar (K3 - olculmus:
 * Tekel kategorisi -101.698 TL, ilk sekiz "zarar" satirinin sekizi de
 * sigara). Ayri bolumde gosterilir ki gercek zarar adaylari bu gurultunun
 * altinda kaybolmasin. `033`'un SQL'ine DOKUNULMAZ - ayrim burada, EKRANDA
 * yapilir (paylasilan/mutabik yuzeyler duran kurali).
 */
const SABIT_FIYATLI_KATEGORILER = ['TEKEL'];

/**
 * Turkce buyutme icin `toLocaleUpperCase('tr-TR')` ZORUNLU -
 * `String.prototype.toUpperCase()` locale'den BAGIMSIZ calisir ve
 * noktali/noktasiz i ciftinde (i/İ) yanlis sonuc uretebilir (ör.
 * 'kuruyemiş'.toUpperCase() -> 'KURUYEMIŞ', tr-TR'de olmasi gereken
 * 'KURUYEMİŞ' degil - node ile dogrulandi). `SABIT_FIYATLI_KATEGORILER`
 * bugun yalniz 'TEKEL' iceriyor ve bu sozcukte i/İ/ı gecmiyor, o yuzden bu
 * ozel kelime icin iki yontem AYNI sonucu verir (rapora bkz.) - yine de
 * dogru yontem kullaniliyor: ileride eklenecek herhangi bir sabit fiyatli
 * kategori adi (ör. 'İLAÇ') bu ayrimdan dogru yararlanabilsin.
 *
 * Kategori adlari basta '*' tasidigindan (ör. '*TEKEL') ESITLIK degil
 * ICERME ile eslesir.
 */
function sabitFiyatliMi(kategori: string): boolean {
  const k = kategori.toLocaleUpperCase('tr-TR');
  return SABIT_FIYATLI_KATEGORILER.some((s) => k.includes(s));
}

/**
 * `gercek_zarar` YALNIZ donem icinde gercekten alis yapilmis urunlerde
 * doludur; alis yoksa SQL NULL doner. NULL "kar" sayilmaz - bilgi yoklugu
 * bir olcum degildir, satir dokunulmadan gercek zarar bolumunde kalir.
 * API sayilari metin olarak dondurebildigi icin Number()'dan gecirilir ve
 * yalniz SONLU degerler kabul edilir (NaN/Infinity ayrim yapmaz).
 */
export function gercekZararOku(satir: Pick<ZararSatiri, 'gercek_zarar'>): number | null {
  const ham = satir.gercek_zarar;
  // NULL/bos: donem icinde alis YOK. `Number(null)` 0 verir ve "0 zarar"
  // olcum gibi gorunur - bu yuzden sayiya cevirmeden ONCE elenir. Bugunku
  // karsilastirma (< 0) bunu zaten gecirmezdi, ama iddia karsilastirmadan
  // BAGIMSIZ: yokluk bir olcum degildir. Dogrudan birim testli.
  if (ham === null || ham === undefined || ham === '') return null;
  const n = Number(ham);
  // Sonsuz/NaN de olcum degildir: '-1e999' -> -Infinity, isaret testini
  // GECER ve ekrana Infinity basardi.
  return Number.isFinite(n) ? n : null;
}

function fmtYuzde1(n: number): string {
  return `${n.toFixed(1).replace('.', ',')}%`;
}

/** Marj = kar / ciro * 100. Ciro 0 ise SIFIRA BOLME yapilmaz, null doner. */
function marjHesapla(kar: number, ciro: number): number | null {
  if (!ciro) return null;
  return (kar / ciro) * 100;
}

function ZararSatirGorunum({ satir }: { satir: ZararSatiri }) {
  return (
    <div
      data-testid={`zarar-satir-${satir.urun}`}
      className="flex items-center justify-between gap-3 text-[12px] text-ink-700"
    >
      <span className="truncate">{satir.urun}</span>
      <span className="flex items-center gap-4 shrink-0 tnum">
        <span className="text-ink-500">{fmtNum(Number(satir.satilan_miktar))} adet</span>
        <span className="text-ink-500">
          {fmtTRY(Number(satir.ort_satis_fiyat))} / {fmtTRY(Number(satir.birim_maliyet))}
        </span>
        <span className="text-neg-700 w-20 text-right">{fmtTRY(Number(satir.zarar))}</span>
      </span>
    </div>
  );
}

/**
 * "Kart fiyati yaniltiyor" satiri: ayni urunu 033'un gordugu gibi degil,
 * donem icindeki GERCEK alislarin hacim agirlikli maliyetiyle gosterir.
 * Kart maliyeti USTU CIZILI basilir ki okuyan hangi sayinin yerini neyin
 * aldigini gorsun; sagdaki tutar ZARAR degil KAR oldugu icin pozitif
 * yazilir (gercek_zarar negatiftir, isareti burada cevrilir).
 */
function YaniltanSatirGorunum({ satir }: { satir: ZararSatiri }) {
  const gercekKar = -Number(satir.gercek_zarar);
  return (
    <div
      data-testid={`zarar-yaniltan-${satir.urun}`}
      className="flex items-center justify-between gap-3 text-[12px] text-ink-700"
    >
      <span className="truncate">{satir.urun}</span>
      <span className="flex items-center gap-4 shrink-0 tnum">
        <span className="text-ink-500">{fmtNum(Number(satir.satilan_miktar))} adet</span>
        <span className="text-ink-500">
          {fmtTRY(Number(satir.ort_satis_fiyat))} /{' '}
          <span className="line-through">{fmtTRY(Number(satir.birim_maliyet))}</span>{' '}
          → {fmtTRY(Number(satir.gercek_birim_maliyet))}
        </span>
        <span className="w-20 text-right">{fmtTRY(gercekKar)} kâr</span>
      </span>
    </div>
  );
}

/**
 * Katman 4 - dikkat seridi. ESIKSIZ: sadece sirali listeler, hicbir sabit
 * sinirla eleme yapilmaz (kalibrasyon dagilimdan yapilir, bu surumun isi
 * degil - bkz. spec §8).
 *
 * Uc bagimsiz kaynak ailesi (033'un kendi icinde ikiye ayrilmasiyla DORT
 * gosterge alani): gercek zarar adaylari (033, sabit fiyatli olmayanlar) ·
 * sabit fiyatli kategoriler (033, aciklamali) · iade yogunlugu (011, sube
 * bazinda iade_oran) · marji eriyen kategoriler (015, SECILEN donem ile
 * AYNI UZUNLUKTAKI hemen onceki donemin farki, bkz. `oncekiDonemHesapla`).
 * Her bolum KENDI kaynagina gore gorunur; hicbiri dolu degilse blok tumden
 * gizlenir (bos durum kurali, KategoriBlok/SubeBlok ile ayni desen).
 */
export function DikkatBlok({ reports, donem, sube = null }: Props) {
  const params = { '@BASTARIH': donem.bas, '@BITTARIH': donem.bit };
  // CONTROLLER RULING F4: sonek + ad kontrolu (bkz. KanalBlok/SubeBlok/
  // KategoriBlok, ayni desen). Ad tutmazsa alt bolum sessizce gizlenir,
  // yanlis firmanin verisini basmaz.
  const raporZarar = raporBul(reports, 33, 'Zararına Satışlar');
  const idZarar = raporZarar?.sourceId ?? undefined;
  // S197: 041 varsa TERCIH EDILIR - 033'un alti kolonunu BIREBIR uretir
  // (canlida iki yonlu EXCEPT ile 0/0 dogrulandi) ve uzerine donem icindeki
  // gercek alistan turetilen maliyeti ekler. Yoksa 033'e duser ve ekran
  // eskisi gibi davranir; boylece diger firmalar etkilenmez.
  const raporZararDog = raporBul(reports, 41, 'Zararına Satışlar (maliyet doğrulamalı)');
  const idZararDog = raporZararDog?.sourceId ?? undefined;
  const idZararKaynak = idZararDog ?? idZarar;
  // Destek, GERCEKTEN kullanilan kaynagin tanimindan okunur (041 varsa o,
  // yoksa 033) - iki kaynagin destegi farkli olabilir.
  const zararDestekli = lokasyonDestekli(idZararDog !== undefined ? raporZararDog : raporZarar);
  const paramsZarar = { ...params, ...lokasyonParam(sube, zararDestekli) };
  const idIade = sourceIdBul(reports, 11, 'İade Analizi');

  const zararQ = useQuery({
    queryKey: ['sa-dikkat-zarar', idZararKaynak, paramsZarar],
    queryFn: () => queryDataSource<ZararSatiri>({ sourceId: idZararKaynak!, parameters: paramsZarar }),
    enabled: !!idZararKaynak,
    throwOnError: false,
  });

  const iadeQ = useQuery({
    queryKey: ['sa-dikkat-iade', idIade, params],
    queryFn: () => queryDataSource<IadeSatiri>({ sourceId: idIade!, parameters: params }),
    enabled: !!idIade,
    throwOnError: false,
  });

  const raporKategori = raporBul(reports, 15, 'Kategori Bazında Satış');
  const idKategori = raporKategori?.sourceId ?? undefined;
  const kategoriDestekli = lokasyonDestekli(raporKategori);
  // 012'nin tanimiyla AYNI turetme (bkz. donem.ts, ayrica birim testli).
  const oncekiDonem = useMemo(() => oncekiDonemHesapla(donem), [donem.bas, donem.bit]);
  const lokEk = lokasyonParam(sube, kategoriDestekli);
  const kategoriParams = { ...params, ...lokEk };
  const oncekiParams = { '@BASTARIH': oncekiDonem.bas, '@BITTARIH': oncekiDonem.bit, ...lokEk };

  const kategoriBuQ = useQuery({
    queryKey: ['sa-dikkat-kategori-bu', idKategori, kategoriParams],
    queryFn: () => queryDataSource<KategoriSatiri>({ sourceId: idKategori!, parameters: kategoriParams }),
    enabled: !!idKategori,
    throwOnError: false,
  });

  const kategoriOncekiQ = useQuery({
    queryKey: ['sa-dikkat-kategori-onceki', idKategori, oncekiParams],
    queryFn: () =>
      queryDataSource<KategoriSatiri>({ sourceId: idKategori!, parameters: oncekiParams }),
    enabled: !!idKategori,
    throwOnError: false,
  });

  const { gercekZarar, sabitFiyatliZarar, kartYaniltiyor } = useMemo(() => {
    const rows = zararQ.data?.data ?? [];
    const gercek: ZararSatiri[] = [];
    const sabit: ZararSatiri[] = [];
    const yaniltan: ZararSatiri[] = [];
    for (const r of rows) {
      // Sabit fiyatli kategori ONCE gelir: sigaranin zarari zaten sistematik
      // ve aciklanmis: gercek maliyetle isareti donse bile o bolumde kalir,
      // yoksa ayni satir iki farkli gerekceyle iki yerde anlatilirdi.
      if (sabitFiyatliMi(String(r.kategori))) {
        sabit.push(r);
        continue;
      }
      const gz = gercekZararOku(r);
      (gz !== null && gz < 0 ? yaniltan : gercek).push(r);
    }
    // Esiksiz: TUM satirlar basilir, yalniz zarara gore AZALAN siralanir.
    const azalan = (a: ZararSatiri, b: ZararSatiri) => Number(b.zarar) - Number(a.zarar);
    return {
      gercekZarar: [...gercek].sort(azalan),
      sabitFiyatliZarar: [...sabit].sort(azalan),
      kartYaniltiyor: [...yaniltan].sort(azalan),
    };
  }, [zararQ.data]);

  const iadeSirali = useMemo(() => {
    const rows = magazaSuz(iadeQ.data?.data ?? [], sube);
    return [...rows].sort((a, b) => Number(b.iade_oran) - Number(a.iade_oran));
  }, [iadeQ.data, sube]);

  // Marji eriyen kategoriler: BU donemin marjini AYNI UZUNLUKTAKI hemen
  // ONCEKI donemle karsilastirir. Yalniz DUSENLER gosterilir (eşiksiz,
  // ama iyilesen bir kategori "dikkat" ogesi degildir - o zaten iyi haber).
  const marjErimesi = useMemo<MarjErimeSatiri[]>(() => {
    const buRows = kategoriBuQ.data?.data ?? [];
    const oncekiRows = kategoriOncekiQ.data?.data ?? [];

    const oncekiMarj = new Map<string, number>();
    for (const r of oncekiRows) {
      const m = marjHesapla(Number(r.kar), Number(r.ciro));
      if (m !== null) oncekiMarj.set(String(r.kategori), m);
    }

    const sonuc: MarjErimeSatiri[] = [];
    for (const r of buRows) {
      const kategoriAdi = String(r.kategori);
      const marjBu = marjHesapla(Number(r.kar), Number(r.ciro));
      if (marjBu === null) continue; // bu donemde ciro=0 -> karsilastirilamaz

      const marjOnceki = oncekiMarj.get(kategoriAdi);
      // Onceki donemde bu kategori YOKSA (yeni kategori) veya onceki ciro=0
      // ise KARSILASTIRILAMAZ. Eksik onceki marji SIFIR SAYMAK sahte/buyuk
      // bir "erime" uretir (ör. marjBu %40 - varsayilan 0 = "%40 eridi"
      // derdi, oysa kiyaslanacak bir onceki deger hic YOK) - bu yuzden
      // KARAR: boyle bir kategori DISLANIR, sifir varsayilmaz.
      if (marjOnceki === undefined) continue;

      const fark = marjBu - marjOnceki;
      if (fark < 0) sonuc.push({ kategori: kategoriAdi, marjBu, marjOnceki, fark });
    }

    // Esiksiz: TUM dusenler basilir, yalniz farka gore ARTAN (en cok
    // eriyen - en negatif fark - once) siralanir.
    return sonuc.sort((a, b) => a.fark - b.fark);
  }, [kategoriBuQ.data, kategoriOncekiQ.data]);

  // Kaynaklardan biri hic cozulmediyse (rapor yok/ad tutmuyor -> id yok) o
  // kaynak "hazir degil" sayilmaz - zaten hicbir zaman sorgulanmayacagi
  // icin sonsuza kadar beklemeyiz (bkz. KategoriBlok, ayni desen).
  const zararHazir = !idZararKaynak || zararQ.data !== undefined;
  const iadeHazir = !idIade || iadeQ.data !== undefined;
  const kategoriHazir =
    !idKategori || (kategoriBuQ.data !== undefined && kategoriOncekiQ.data !== undefined);

  const gercekGorunur = zararHazir && gercekZarar.length > 0;
  const sabitGorunur = zararHazir && sabitFiyatliZarar.length > 0;
  const yaniltanGorunur = zararHazir && kartYaniltiyor.length > 0;
  const iadeGorunur = iadeHazir && iadeSirali.length > 0;
  const marjGorunur = kategoriHazir && marjErimesi.length > 0;

  if (!idZararKaynak && !idIade && !idKategori) return null; // hicbir kaynak yok -> sorgu bile acilmaz
  if (!gercekGorunur && !sabitGorunur && !yaniltanGorunur && !iadeGorunur && !marjGorunur) return null; // hicbiri dolu degil -> blok gizlenir

  return (
    <div data-testid="blok-dikkat">
      <SectionCard title="Dikkat" subtitle="Eşiksiz, sıralı listeler — henüz uyarı değil">
        <div className="flex flex-col gap-5">
          {gercekGorunur && (
            <div data-testid="zarar-gercek" className="flex flex-col gap-1.5">
              <h3 className="text-[12px] font-semibold text-ink-700">
                Gerçek zarar adayları
                <KapsamNotu sube={sube} destekli={zararDestekli} />
              </h3>
              {gercekZarar.map((r) => (
                <ZararSatirGorunum key={r.urun} satir={r} />
              ))}
            </div>
          )}

          {yaniltanGorunur && (
            <div data-testid="zarar-kart-yaniltiyor" className="flex flex-col gap-1.5">
              <h3 className="text-[12px] font-semibold text-ink-700">
                Kart fiyatı yanıltıyor — bunlar zarar DEĞİL
                <KapsamNotu sube={sube} destekli={zararDestekli} />
              </h3>
              <p className="text-[12px] text-ink-500">
                Zarar hesabı stok kartındaki <strong>son alış</strong> fiyatını kullanır; o
                fiyat en son partinin fiyatıdır, hacme bakmaz. Dönem içinde gerçekten alınan
                miktarın <strong>hacim ağırlıklı</strong> maliyetiyle bu ürünler kâr ediyor.
              </p>
              {kartYaniltiyor.map((r) => (
                <YaniltanSatirGorunum key={r.urun} satir={r} />
              ))}
            </div>
          )}

          {sabitGorunur && (
            <div data-testid="zarar-sabit-fiyatli" className="flex flex-col gap-1.5">
              <h3 className="text-[12px] font-semibold text-ink-700">
                Sabit fiyatlı kategoriler (Tekel)
                <KapsamNotu sube={sube} destekli={zararDestekli} />
              </h3>
              <p className="text-[12px] text-ink-500">
                Devlet-belirlenmiş etiket fiyatından satılır; zam sonrası son alış fiyatı
                satış anındaki eski etiketin üzerine çıkar — sistematik ama gerçek olmayan
                zarar, bu yüzden ayrı gösterilir.
              </p>
              {sabitFiyatliZarar.map((r) => (
                <ZararSatirGorunum key={r.urun} satir={r} />
              ))}
            </div>
          )}

          {iadeGorunur && (
            <div data-testid="iade-yogunlugu" className="flex flex-col gap-1.5">
              {/* 011 `magaza` kolonu tasiyor -> gercekten suzuluyor, not YOK. */}
              <h3 className="text-[12px] font-semibold text-ink-700">İade yoğunluğu</h3>
              {iadeSirali.map((s) => (
                <div
                  key={s.magaza}
                  data-testid={`iade-satir-${s.magaza}`}
                  className="flex items-center justify-between gap-3 text-[12px] text-ink-700"
                >
                  <span className="truncate">{s.magaza}</span>
                  <span className="flex items-center gap-4 shrink-0 tnum">
                    <span className="text-ink-500">{fmtNum(Number(s.iade_fis))} fiş</span>
                    <span>{fmtTRY(Number(s.iade_tutar))}</span>
                    <span className="w-14 text-right">{fmtYuzde1(Number(s.iade_oran))}</span>
                  </span>
                </div>
              ))}
            </div>
          )}

          {marjGorunur && (
            <div data-testid="marj-eriyen" className="flex flex-col gap-1.5">
              <h3 className="text-[12px] font-semibold text-ink-700">
                Marjı eriyen kategoriler
                <KapsamNotu sube={sube} destekli={kategoriDestekli} />
              </h3>
              <p className="text-[12px] text-ink-500">
                Önceki, eşit uzunluktaki döneme göre marj puanı düşen kategoriler.
              </p>
              {marjErimesi.map((m) => (
                <div
                  key={m.kategori}
                  data-testid={`marj-eriyen-satir-${m.kategori}`}
                  className="flex items-center justify-between gap-3 text-[12px] text-ink-700"
                >
                  <span className="truncate">{m.kategori}</span>
                  <span className="flex items-center gap-4 shrink-0 tnum">
                    <span className="text-ink-500">
                      {fmtYuzde1(m.marjOnceki)} → {fmtYuzde1(m.marjBu)}
                    </span>
                    <span className="text-neg-700 w-20 text-right">{fmtYuzde1(m.fark)} puan</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
